<template>
    <!-- шапка в новом дизайне (как на новом сайте): тёмно-синяя, меню, личный кабинет, выбор языка.
         На главной рабочего сайта пока старый баннер, поэтому шапка везде сплошная, не поверх баннера,
         а на главной под ней отступ — старый баннер рассчитан на прежнюю белую шапку с полями -->
    <div class="w-full relative z-40 site-header--dark site-header--solid" :class="{ 'mb-4 lg:mb-6': isHome }">
        <header class="my-container h-16 lg:h-[72px] flex justify-between items-center gap-4">
            <a :href="localePath('/')" class="shrink-0">
                <!-- тот же логотип, что в подвале: белая надпись, цветные волны -->
                <img src="/images/brand/logo-white.png" alt="Qanot Sharq" class="site-header__logo" />
            </a>

            <nav class="hidden lg:flex items-center gap-8" :aria-label="t('menu')">
                <UDropdown mode="hover" :items="destinationsMenu" :popper="{ placement: 'bottom-start' }" :ui="dropdownUi">
                    <button type="button" class="site-header__link">
                        {{ t('navDestinations') }}
                        <Icon name="heroicons:chevron-down-20-solid" size="18" />
                    </button>
                </UDropdown>
                <a :href="MY_BOOKING_URL" target="_blank" rel="noopener" class="site-header__link">{{ t('navMyBooking') }}</a>
                <UDropdown mode="hover" :items="informationMenu" :popper="{ placement: 'bottom-start' }" :ui="dropdownUi">
                    <button type="button" class="site-header__link">
                        {{ t('information') }}
                        <Icon name="heroicons:chevron-down-20-solid" size="18" />
                    </button>
                </UDropdown>
            </nav>

            <div class="flex items-center">
                <!-- личный кабинет — модуль Account WX; обычная ссылка: страница модуля открывается полной загрузкой -->
                <a :href="localePath('/account')" class="site-header__account" :aria-label="t('myAccount')">
                    <Icon name="heroicons:user" size="18" class="block shrink-0" />
                    <span class="hidden sm:inline">{{ t('myAccount') }}</span>
                </a>
                <ApplicationLanguage dark />
                <button type="button" class="lg:hidden ml-2 sm:ml-3 p-1.5 -mr-1.5 rounded-lg transition-colors text-white hover:bg-white/10"
                    :aria-label="t('menu')" @click="menuOpen = true">
                    <Icon name="heroicons:bars-3" size="26" class="block" />
                </button>
            </div>
        </header>

        <!-- меню на телефоне и планшете -->
        <USlideover v-model="menuOpen" side="left"
            :ui="{ width: 'w-screen max-w-sm', overlay: { background: 'bg-black/40' } }">
            <div class="p-6 overflow-y-auto">
                <div class="flex items-center justify-between mb-6">
                    <img src="/logo.png" alt="Qanot Sharq" class="h-5" />
                    <button type="button" @click="menuOpen = false" :aria-label="t('close')"
                        class="p-2 rounded-lg bg-[#F4F5F6] hover:bg-[#EEF1F6] transition-colors">
                        <Icon name="heroicons:x-mark" size="20" class="block" />
                    </button>
                </div>
                <a :href="localePath('/account')" class="mobile-menu__link font-semibold">{{ t('myAccount') }}</a>
                <a :href="MY_BOOKING_URL" target="_blank" rel="noopener" class="mobile-menu__link font-semibold">{{ t('navMyBooking') }}</a>
                <div class="mobile-menu__title">{{ t('navDestinations') }}</div>
                <NuxtLink v-for="item in destinationsMenu.flat()" :key="item.label" :to="item.to"
                    class="mobile-menu__link" @click="menuOpen = false">{{ item.label }}</NuxtLink>
                <template v-for="group in informationMenu" :key="group[0].to">
                    <div class="mobile-menu__title">{{ group === informationMenu[0] ? t('forPassengers') : t('company') }}</div>
                    <NuxtLink v-for="item in group" :key="item.label" :to="item.to" class="mobile-menu__link"
                        @click="menuOpen = false">{{ item.label }}</NuxtLink>
                </template>
            </div>
        </USlideover>
    </div>
</template>

<script setup>
const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const isHome = computed(() => route.path === localePath('/'))

// «Моё бронирование» — как «Управление бронированием» на главной: поиск заказа в прежней системе
// (на время перехода на WX её не трогаем — так просили WX)
const MY_BOOKING_URL = 'https://booking.qanotsharq.com/websky_grs/#/search-order'

// меню
const menuOpen = ref(false)
watch(() => route.fullPath, () => menuOpen.value = false)
const destinationsMenu = computed(() => [[
    { label: t('allDestinations'), to: localePath('/flights') },
    { label: t('onlineTable'), to: localePath('/online-table') },
]])
const informationMenu = computed(() => [
    [
        { label: t('tariffs'), to: localePath('/tariffs') },
        { label: t('shippingRules'), to: localePath('/information/transportation-rules') },
        { label: t('luggageShipping'), to: localePath('/information/baggage-transportation') },
        { label: t('excessLuggage'), to: localePath('/extra-luggage') },
        { label: t('passengerCategory'), to: localePath('/passenger-categories') },
        { label: t('registration'), to: localePath('/information/check-in-for-the-flight') },
        { label: t('placeChoose'), to: localePath('/information/choosing-a-place') },
        { label: t('orderFood'), to: localePath('/information/on-board-service') },
        { label: t('aq'), to: localePath('/faq') },
    ],
    [
        { label: t('aboutUs'), to: localePath('/about') },
        { label: t('ourFleet'), to: localePath('/planes') },
        { label: t('news'), to: localePath('/news') },
        { label: t('vacancy'), to: localePath('/vacancy') },
    ],
])
const dropdownUi = {
    width: 'w-64',
    rounded: 'rounded-xl',
    ring: 'ring-1 ring-gray-line',
    shadow: 'shadow-[0_12px_32px_rgba(16,24,40,0.14)]',
    divide: 'divide-y divide-gray-line',
    padding: 'p-1.5',
    item: {
        padding: 'px-3 py-2',
        size: 'text-[14px]/[18px]',
        rounded: 'rounded-lg',
        active: 'bg-[#F4F5F6] text-black',
        inactive: 'text-black',
    },
}
</script>

<style lang="scss">
.site-header__logo {
    @apply h-5 sm:h-6 w-auto;
}

.site-header--dark {
    background: rgba(10, 24, 56, 0.55);
    backdrop-filter: blur(10px) saturate(1.15);
    -webkit-backdrop-filter: blur(10px) saturate(1.15);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

// сплошной тёмно-синий — под шапкой нет баннера с небом
.site-header--solid {
    background: #12285a;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
}

.site-header__link {
    @apply flex items-center gap-1 py-2 text-[15px]/[20px] text-black transition-colors hover:text-my-green;

    .site-header--dark & {
        @apply text-white/90 hover:text-white;
    }
}

// «Личный кабинет», как на aeroflot.ru: «таблетка» со значком, на тёмной шапке — светлая
.site-header__account {
    @apply flex items-center gap-1.5 mr-1 sm:mr-2 px-2 sm:px-3 py-1.5 rounded-lg bg-[#F4F5F6] hover:bg-[#EEF1F6] text-[14px]/[17px] font-medium transition-colors;

    .site-header--dark & {
        @apply bg-white/10 hover:bg-white/20 text-white;
    }
}

.mobile-menu__title {
    @apply mt-6 mb-1 text-[13px]/[16px] uppercase tracking-wide text-gray-text;
}

.mobile-menu__link {
    @apply block py-2.5 text-[16px]/[20px] border-b border-gray-line hover:text-my-green transition-colors;
}
</style>
